import React, { useCallback, useEffect, useState } from 'react';
import {
  listCameraNotificationGroups,
  createNotificationGroup,
  updateNotificationGroup,
  deleteNotificationGroup,
  listNotificationEventCatalog,
  listRecipientCandidates,
} from '../../services/notifications';
import Modal from 'react-bootstrap/Modal';
import { Xmark } from '@gravity-ui/icons';
import { countRu } from '../../utils/pluralRu';
import './CameraNotificationGroups.css';

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

// Каталог событий приходит из untyped-ответа; имя поля с кодом события может
// отличаться (event_code/code/event/…). Приводим к стабильной форме, иначе
// undefined-код у всех пунктов делает includes(undefined) истинным для всех
// сразу — выбор одного события «отмечал» все.
const CODE_KEYS = ['event_code', 'code', 'event', 'value', 'id', 'name'];
const LABEL_KEYS = ['label', 'title', 'display_name', 'name'];

const resolveCode = (c) => {
  for (const k of CODE_KEYS) {
    if (c && c[k] != null && c[k] !== '') return String(c[k]);
  }
  return null;
};

const normalizeCatalog = (data) => {
  const seen = new Set();
  const out = [];
  for (const c of normalizeList(data)) {
    const code = resolveCode(c);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    const label = LABEL_KEYS.map((k) => c[k]).find((v) => v != null && v !== '') || code;
    const emailSupported = c.email_supported ?? c.emailSupported ?? c.supports_email;
    out.push({ code, label, emailSupported });
  }
  return out;
};

const normalizeEmail = (email) => (email || '').trim().toLowerCase();

/*
 * RECIPIENT-CHANNELS01 (ТЗ v4 §28, FE-FIX25): у получателя два канала.
 *
 * portal_user может быть Email-only, Push-only или оба; manual_email — канал по
 * определению email-only, Web Push ему не предлагаем даже неактивным «на
 * будущее», иначе получатель выглядит как portal_user.
 *
 * Флаги отправляются всегда явно: прежнее поведение «поле не послали — значит
 * email» больше не считается формой управления каналом.
 */
const RECIPIENT_TYPE_LABEL = {
  portal_user: 'Пользователь портала',
  manual_email: 'Email-получатель',
};

const recipientType = (r) => r?.recipient_type || (r?.user_id ? 'portal_user' : 'manual_email');

const isPortalUser = (r) => recipientType(r) === 'portal_user';

/**
 * Эффективное состояние каналов из ответа backend. Отсутствующий email_enabled
 * трактуем как включённый: так выглядят группы, созданные до появления каналов,
 * и они действительно рассылали email. Web Push без флага — выключен: канал
 * новый, включённым «по умолчанию» он не бывает.
 */
const normalizeRecipient = (r) => {
  const type = recipientType(r);
  const portal = type === 'portal_user';
  return {
    ...r,
    recipient_type: type,
    email_enabled: r?.email_enabled ?? true,
    web_push_enabled: portal ? (r?.web_push_enabled ?? false) : false,
  };
};

const toRecipientRequest = (r) => {
  const body = {
    recipient_type: r.recipient_type,
    display_name: r.display_name,
    email_enabled: Boolean(r.email_enabled),
    web_push_enabled: isPortalUser(r) ? Boolean(r.web_push_enabled) : false,
  };
  if (r.user_id != null) body.user_id = r.user_id;
  if (r.email) body.email = r.email;
  return body;
};

const describeRecipientChannels = (r) => {
  const channels = [];
  if (r.email_enabled) channels.push('Email');
  if (isPortalUser(r) && r.web_push_enabled) channels.push('WEB PUSH');
  return channels.length > 0 ? channels.join(' + ') : 'без каналов';
};

/*
 * Подпись события для пользователя (ТЗ v4 §28): «Нарушение подтверждено» —
 * внутренний смысл кода, но в интерфейсе это «Уведомление о событиях».
 * Переименовывается только подпись: event_code и DTO остаются как есть.
 */
const EVENT_LABEL_OVERRIDES = {
  violation_confirmed: 'Уведомление о событиях',
  'Нарушение подтверждено': 'Уведомление о событиях',
};

const eventLabel = (c) => EVENT_LABEL_OVERRIDES[c.code] || EVENT_LABEL_OVERRIDES[c.label] || c.label;

const CHANNEL_ID_EMAIL = (i) => 'rc_email_' + i;
const CHANNEL_ID_PUSH = (i) => 'rc_push_' + i;

const emptyDraft = () => ({ display_name: '', enabled: true, recipients: [], event_codes: [], version: undefined, id: null });

const CameraNotificationGroups = ({ cameraId }) => {
  const [groups, setGroups] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [draft, setDraft] = useState(null);
  const [manualEmail, setManualEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    listCameraNotificationGroups(cameraId)
      .then((data) => setGroups(normalizeList(data)))
      .catch(() => setError('Не удалось загрузить группы'))
      .finally(() => setLoading(false));
  }, [cameraId]);

  useEffect(() => {
    reload();
    listNotificationEventCatalog().then((d) => setCatalog(normalizeCatalog(d))).catch(() => setCatalog([]));
    listRecipientCandidates().then((d) => setCandidates(normalizeList(d))).catch(() => setCandidates([]));
  }, [reload]);

  const openCreate = () => { setDraft(emptyDraft()); setError(''); };
  const openEdit = (g) => {
    setDraft({
      id: g.id,
      display_name: g.display_name || '',
      enabled: g.enabled !== false,
      recipients: (g.recipients || []).map(normalizeRecipient),
      event_codes: [...(g.event_codes || [])],
      version: g.version,
    });
    setError('');
  };
  const closeModal = () => { setDraft(null); setManualEmail(''); };

  const toggleEvent = (code) => setDraft((d) => ({
    ...d,
    event_codes: d.event_codes.includes(code) ? d.event_codes.filter((c) => c !== code) : [...d.event_codes, code],
  }));

  const addManualEmail = () => {
    const email = normalizeEmail(manualEmail);
    if (!email) return;
    const exists = draft.recipients.some((r) => normalizeEmail(r.email) === email);
    if (exists) { setManualEmail(''); return; }
    setDraft((d) => ({
      ...d,
      recipients: [
        ...d.recipients,
        { recipient_type: 'manual_email', email, display_name: email, email_enabled: true, web_push_enabled: false },
      ],
    }));
    setManualEmail('');
  };

  const addCandidate = (cand) => {
    const exists = draft.recipients.some((r) => r.user_id && r.user_id === cand.user_id);
    if (exists) return;
    // Новый получатель получает только Email: Push включает администратор явно.
    setDraft((d) => ({
      ...d,
      recipients: [
        ...d.recipients,
        {
          recipient_type: 'portal_user',
          user_id: cand.user_id,
          display_name: cand.display_name || cand.email,
          email_enabled: true,
          web_push_enabled: false,
        },
      ],
    }));
  };

  const removeRecipient = (idx) => setDraft((d) => ({ ...d, recipients: d.recipients.filter((_, i) => i !== idx) }));

  const toggleRecipientChannel = (idx, key) => setDraft((d) => ({
    ...d,
    recipients: d.recipients.map((r, i) => {
      if (i !== idx) return r;
      // manual_email остаётся email-only: Web Push ему не переключить.
      if (key === 'web_push_enabled' && !isPortalUser(r)) return r;
      return { ...r, [key]: !r[key] };
    }),
  }));

  const save = () => {
    setError('');
    const body = {
      display_name: draft.display_name,
      enabled: draft.enabled,
      recipients: draft.recipients.map(toRecipientRequest),
      event_codes: draft.event_codes,
    };
    const req = draft.id
      ? updateNotificationGroup(cameraId, draft.id, { ...body, version: draft.version })
      : createNotificationGroup(cameraId, body);

    req.then(() => { closeModal(); reload(); })
      .catch((err) => {
        if (err?.response?.status === 409) {
          setError('Группа была изменена другим администратором. Список перезагружен — примените изменения заново.');
          reload();
        } else {
          setError('Не удалось сохранить группу');
        }
      });
  };

  const remove = (g) => {
    if (!window.confirm(`Удалить группу «${g.display_name}»?`)) return;
    deleteNotificationGroup(cameraId, g.id, g.version)
      .then(reload)
      .catch((err) => {
        if (err?.response?.status === 409) { setError('Конфликт версии при удалении — список перезагружен.'); reload(); }
        else setError('Не удалось удалить группу');
      });
  };

  // Каталог уже нормализован. Если флаг email_supported присутствует —
  // показываем только поддерживающие email; если его нет ни у одного пункта
  // (иное имя поля в ответе) — показываем все, чтобы список не был пустым.
  const anyEmailFlag = catalog.some((c) => c.emailSupported != null);
  const emailSupported = anyEmailFlag ? catalog.filter((c) => c.emailSupported === true) : catalog;

  return (
    <div className="camera-notification-groups">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Группы уведомлений</h6>
        <button type="button" className="btn btn-sm btn-primary" onClick={openCreate}>+ Группа</button>
      </div>

      {error && <div className="alert alert-warning py-2">{error}</div>}
      {loading && <div className="text-muted">Загрузка…</div>}
      {!loading && groups.length === 0 && <div className="text-muted">Групп нет</div>}

      <ul className="list-unstyled">
        {groups.map((g) => (
          <li key={g.id} className="notif-group border rounded p-2 mb-2">
            <div className="d-flex justify-content-between">
              <strong>{g.display_name}</strong>
              <span className={`badge bg-${g.enabled !== false ? 'success' : 'secondary'}`}>
                {g.enabled !== false ? 'Включена' : 'Выключена'}
              </span>
            </div>
            <div className="small text-muted">{countRu((g.recipients || []).length, ['получатель', 'получателя', 'получателей'])}</div>
            {(g.recipients || []).length > 0 && (
              <ul className="list-unstyled small text-muted mb-0">
                {(g.recipients || []).map(normalizeRecipient).map((r, i) => (
                  <li key={r.user_id || r.email || i}>
                    {r.display_name || r.email || r.user_id} — {describeRecipientChannels(r)}
                  </li>
                ))}
              </ul>
            )}
            <div className="small text-muted">{countRu((g.event_codes || []).length, ['тип события', 'типа событий', 'типов событий'])}</div>
            <div className="mt-2 d-flex gap-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(g)}>Изменить</button>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(g)}>Удалить</button>
            </div>
          </li>
        ))}
      </ul>

      {draft && (
        <Modal show onHide={closeModal} centered scrollable className="notif-modal admin-modal" aria-labelledby="notif-modal-title">
          <div className="modal-header">
            <h5 className="modal-title" id="notif-modal-title">{draft.id ? 'Изменить группу' : 'Новая группа'}</h5>
            <button type="button" className="btn-close" onClick={closeModal} title="Закрыть" aria-label="Закрыть" />
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label" htmlFor="notif_name">Название</label>
              <input id="notif_name" className="form-control" maxLength={120}
                value={draft.display_name}
                onChange={(e) => setDraft((d) => ({ ...d, display_name: e.target.value }))} />
            </div>
            <div className="form-check form-switch mb-3">
              <input className="form-check-input" type="checkbox" id="notif_enabled"
                checked={draft.enabled}
                onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))} />
              <label className="form-check-label" htmlFor="notif_enabled">Включена</label>
            </div>

            <div className="mb-3">
              <label className="form-label">События (только с email)</label>
              <div className="notif-events">
              {emailSupported.length === 0 && <div className="small text-muted">Событий с email нет</div>}
              {emailSupported.map((c) => (
                <div className="form-check" key={c.code}>
                  <input className="form-check-input" type="checkbox" id={`ev_${c.code}`}
                    checked={draft.event_codes.includes(c.code)}
                    onChange={() => toggleEvent(c.code)} />
                  <label className="form-check-label" htmlFor={`ev_${c.code}`}>{eventLabel(c)}</label>
                </div>
              ))}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Получатели</label>
              <ul className="list-unstyled">
                {draft.recipients.map((r, i) => (
                  <li key={i} className="notif-recipient border-bottom py-2">
                    <div className="d-flex justify-content-between">
                      <span>
                        {r.display_name || r.email || r.user_id}
                        <span className="d-block small text-muted">{RECIPIENT_TYPE_LABEL[recipientType(r)]}</span>
                      </span>
                      <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => removeRecipient(i)}><Xmark width={14} height={14} /></button>
                    </div>
                    <div className="d-flex gap-3 mt-1">
                      <div className="form-check mb-0">
                        <input className="form-check-input" type="checkbox" id={CHANNEL_ID_EMAIL(i)}
                          checked={Boolean(r.email_enabled)}
                          onChange={() => toggleRecipientChannel(i, 'email_enabled')} />
                        <label className="form-check-label" htmlFor={CHANNEL_ID_EMAIL(i)}>Email</label>
                      </div>
                      {isPortalUser(r) ? (
                        <div className="form-check mb-0">
                          <input className="form-check-input" type="checkbox" id={CHANNEL_ID_PUSH(i)}
                            checked={Boolean(r.web_push_enabled)}
                            onChange={() => toggleRecipientChannel(i, 'web_push_enabled')} />
                          <label className="form-check-label" htmlFor={CHANNEL_ID_PUSH(i)}>WEB PUSH</label>
                        </div>
                      ) : (
                        <span className="small text-muted">WEB PUSH недоступен для email-получателя</span>
                      )}
                    </div>
                    {!r.email_enabled && !(isPortalUser(r) && r.web_push_enabled) && (
                      <div className="small text-warning">Ни один канал не выбран — уведомления не придут.</div>
                    )}
                  </li>
                ))}
              </ul>
              <div className="input-group input-group-sm">
                <input className="form-control" placeholder="email@example.com" value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)} />
                <button type="button" className="btn btn-outline-secondary" onClick={addManualEmail}>Добавить email</button>
              </div>
              {candidates.length > 0 && (
                <div className="mt-2 small">
                  {candidates.slice(0, 10).map((cand) => (
                    <button key={cand.user_id} type="button" className="btn btn-sm btn-outline-primary me-1 mb-1" onClick={() => addCandidate(cand)}>
                      + {cand.display_name || cand.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeModal}>Отмена</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={!draft.display_name.trim()}>Сохранить</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CameraNotificationGroups;
